import type { Account, AccountPlatform } from '../../../entities/account';
import { getAccountStatus } from '../../../entities/account/lib/status';

export type StatusFilter = 'all' | 'active' | 'inactive';
export type PlatformChip = 'all' | AccountPlatform;

/** Порядок чипов на странице «Аккаунты». */
export const PLATFORM_CHIPS: { value: PlatformChip; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'threads', label: 'Threads' },
];

/** Фильтр страницы «Аккаунты» — весь живёт в адресе: ссылку можно переслать. */
export interface AccountsFilter {
  platform: PlatformChip;
  status: StatusFilter;
  query: string;
  tags: string[];
}

const PLATFORMS = new Set<string>(PLATFORM_CHIPS.map((c) => c.value));

export function parseAccountsFilter(params: URLSearchParams): AccountsFilter {
  const status = params.get('status');
  const platform = params.get('platform');
  return {
    // Старые ссылки: «TikTok» был вкладкой статуса (?status=tiktok).
    platform: status === 'tiktok' ? 'tiktok' : platform && PLATFORMS.has(platform) ? (platform as PlatformChip) : 'all',
    status: status === 'active' || status === 'inactive' ? status : 'all',
    query: params.get('q') ?? '',
    tags: (params.get('tags') ?? '').split(',').map((t) => t.trim()).filter(Boolean),
  };
}

/** Новые параметры поверх старых: чужие ключи адреса не трогаем. */
export function writeAccountsFilter(prev: URLSearchParams, filter: AccountsFilter): URLSearchParams {
  const params = new URLSearchParams(prev);
  const set = (key: string, value: string, empty: string) => {
    if (value === empty) params.delete(key);
    else params.set(key, value);
  };
  set('platform', filter.platform, 'all');
  set('status', filter.status, 'all');
  set('q', filter.query, '');
  set('tags', filter.tags.join(','), '');
  return params;
}

const platformOf = (a: Account): AccountPlatform => a.platform ?? 'instagram';

/** Поиск и тэги — без платформы и статуса: из них считаются счётчики чипов. */
function matchesSearchAndTags(a: Account, filter: AccountsFilter): boolean {
  const q = filter.query.trim().toLowerCase().replace(/^@/, '');
  if (q && !`${a.username ?? ''} ${a.name ?? ''}`.toLowerCase().includes(q)) return false;
  // Любой из выбранных тэгов.
  if (filter.tags.length > 0 && !filter.tags.some((t) => (a.tags ?? []).includes(t))) return false;
  return true;
}

export function filterAccounts(accounts: Account[], filter: AccountsFilter): Account[] {
  return accounts.filter((a) =>
    matchesSearchAndTags(a, filter)
    && (filter.platform === 'all' || platformOf(a) === filter.platform)
    && (filter.status === 'all' || getAccountStatus(a) === filter.status));
}

/**
 * Счётчики чипов. Платформы — при текущем статусе, статусы — при текущей
 * платформе: число на чипе = сколько карточек будет, если его нажать.
 * Инвариант: сумма платформ === all, active + inactive === all своей платформы.
 */
export function computeAccountCounts(accounts: Account[], filter: AccountsFilter): {
  platform: Record<PlatformChip, number>;
  status: Record<StatusFilter, number>;
} {
  const base = accounts.filter((a) => matchesSearchAndTags(a, filter));
  const platform = Object.fromEntries(PLATFORM_CHIPS.map((c) => [c.value, 0])) as Record<PlatformChip, number>;
  for (const a of base) {
    if (filter.status !== 'all' && getAccountStatus(a) !== filter.status) continue;
    platform.all += 1;
    platform[platformOf(a)] += 1;
  }
  const inPlatform = base.filter((a) => filter.platform === 'all' || platformOf(a) === filter.platform);
  const active = inPlatform.filter((a) => getAccountStatus(a) === 'active').length;
  return {
    platform,
    status: { all: inPlatform.length, active, inactive: inPlatform.length - active },
  };
}
